import { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';
import { Filter, FilterableField, FilterOperator } from './types';
import { getFieldIcon } from './icons';

interface FilterChipProps {
  filter: Filter;
  field: FilterableField;
  onUpdate: (filter: Filter) => void;
  onRemove: () => void;
}

function isUnaryOperator(operator: FilterOperator | null, fieldType: string): boolean {
  if (!operator) return false;
  return operator.value === 'is_unknown' || operator.value === 'has_any_value' || fieldType === 'boolean';
}

export function FilterChip({ filter, field, onUpdate, onRemove }: FilterChipProps) {
  const [isEditing, setIsEditing] = useState(!filter.operator);
  const containerRef = useRef<HTMLDivElement>(null);

  const Icon = getFieldIcon(field);
  const selectedOperator = field.operators.find((op) => op.value === filter.operator);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsEditing(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatValue = () => {
    if (!selectedOperator) return '';
    if (selectedOperator.value === 'is_unknown') return 'desconhecido';
    if (selectedOperator.value === 'has_any_value') return 'qualquer valor';
    if (field.type === 'boolean') return '';
    if (selectedOperator.inputType === 'days_ago') return `${filter.value} dias`;
    return filter.value?.toString() || '';
  };

  const renderValueInput = (operator: FilterOperator) => {
    if (isUnaryOperator(operator, field.type)) return null;

    // List type: render select with options
    if (field.type === 'list' && field.listOptions && (operator.value === 'is' || operator.value === 'is_not')) {
      return (
        <div className="mt-2 px-4">
          <select
            value={filter.value?.toString() ?? ''}
            onChange={(e) => onUpdate({ ...filter, value: e.target.value || null })}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            style={{ outline: 'none', boxShadow: 'none' }}
          >
            <option value="">Selecione...</option>
            {field.listOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      );
    }

    // Date relative input (days ago)
    if (operator.inputType === 'days_ago') {
      return (
        <div className="flex items-center gap-2 mt-2 px-4">
          <input
            type="number"
            min="1"
            value={filter.value ?? ''}
            onChange={(e) => onUpdate({ ...filter, value: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="w-16 px-3 py-2 text-sm border border-gray-200 rounded-lg"
            style={{ outline: 'none', boxShadow: 'none' }}
            placeholder="1"
          />
          <span className="text-sm text-gray-600">dias</span>
        </div>
      );
    }

    // Date absolute input
    if (operator.inputType === 'date') {
      return (
        <div className="mt-2 px-4">
          <input
            type="date"
            value={filter.value ?? ''}
            onChange={(e) => onUpdate({ ...filter, value: e.target.value || null })}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            style={{ outline: 'none', boxShadow: 'none' }}
          />
        </div>
      );
    }

    // Numeric input
    if (field.type === 'numeric') {
      return (
        <div className="mt-2 px-4">
          <input
            type="number"
            value={filter.value ?? ''}
            onChange={(e) => onUpdate({ ...filter, value: e.target.value ? parseInt(e.target.value, 10) : null })}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
            style={{ outline: 'none', boxShadow: 'none' }}
            placeholder="0"
          />
        </div>
      );
    }

    // Default text input
    return (
      <div className="mt-2 px-4">
        <input
          type="text"
          value={filter.value ?? ''}
          onChange={(e) => onUpdate({ ...filter, value: e.target.value || null })}
          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg"
          style={{ outline: 'none', boxShadow: 'none' }}
          placeholder=""
          autoFocus
        />
      </div>
    );
  };

  return (
    <div ref={containerRef} className="relative inline-flex">
      <div
        onClick={() => setIsEditing(true)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 hover:bg-gray-200 rounded-full cursor-pointer transition-colors"
      >
        <Icon className="w-3.5 h-3.5 text-gray-600" />
        <span className="text-sm text-gray-700">{field.label}</span>
        {selectedOperator && (
          <>
            <span className="text-sm text-gray-500">{selectedOperator.label}</span>
            {formatValue() && (
              <span className="text-sm font-medium text-gray-800">{formatValue()}</span>
            )}
          </>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="ml-0.5 p-0.5 hover:bg-gray-300 rounded-full transition-colors"
        >
          <X className="w-3 h-3 text-gray-500" />
        </button>
      </div>

      {isEditing && (
        <div className="absolute top-full left-0 mt-1 py-3 bg-white rounded-lg shadow-lg border border-gray-200 z-50 min-w-[280px]">
          {/* Operator radio list */}
          <div className="space-y-1">
            {field.operators.map((operator) => {
              const isSelected = filter.operator === operator.value;
              return (
                <div key={operator.value}>
                  <label
                    className="flex items-center gap-3 px-4 py-1.5 cursor-pointer hover:bg-gray-50"
                    onClick={() => {
                      onUpdate({
                        ...filter,
                        operator: operator.value,
                        value: isUnaryOperator(operator, field.type) ? null : filter.value,
                      });
                    }}
                  >
                    <div
                      className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                        isSelected ? 'border-blue-500' : 'border-gray-300'
                      }`}
                    >
                      {isSelected && <div className="w-2 h-2 rounded-full bg-blue-500" />}
                    </div>
                    <span className="text-sm text-gray-700">{operator.label}</span>
                  </label>
                  {/* Show value input below selected operator */}
                  {isSelected && renderValueInput(operator)}
                </div>
              );
            })}
          </div>

          {/* Done button */}
          <div className="mt-3 px-4">
            <button
              type="button"
              onClick={() => setIsEditing(false)}
              className="w-full py-2 text-sm font-medium text-orange-500 hover:bg-orange-50 rounded transition-colors"
            >
              Pronto
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
